import { Avatar, Box, Typography, Button } from "@mui/material";
import React from "react";
import { formatTransactionDate } from "./utils/formatTime";

type HistoryEntry = {
  date: string;
  code: string;
  type: "EARN" | "REDEMPTION";
  points: number;
};

type PlumberInfo = {
  phone: string;
  name?: string;
  address?: string;
  wallet_balance?: number;
  total_earned?: number;
  history?: HistoryEntry[];
};

interface ProfileSectionProps {
  plumber: PlumberInfo | null;
  error?: string | null;
}

const ProfileSection = ({ plumber, error }: ProfileSectionProps) => {
  // determine if we should show the unregistered view. some responses
  // occasionally come back as an object containing an `error` field
  // even if the HTTP status was 200, so guard against that as well.
  const serverErrorMessage = plumber && (plumber as any).error;
  const showUnregistered = error === 'Unregistered' || !!serverErrorMessage;

  if (showUnregistered) {
    return (
      <Box
        sx={{
          flex: 1,
          background: "transparent",
          borderLeft: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#8696a1",
          padding: "16px",
          textAlign: "center",
          flexDirection: "column",
        }}
      >
        <Typography variant="body2" sx={{ marginBottom: '8px' }}>
          {serverErrorMessage || 'User not registered'}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component="a"
          href="/register"
        >
          Register User
        </Button>
      </Box>
    );
  }

  if (!plumber) {
    return (
      <Box
          sx={{
            flex: 1,
            background: "transparent",
            borderLeft: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#8696a1",
            padding: "16px",
            textAlign: "center",
          }}
        >
        <Typography variant="body2">Select a contact to view details</Typography>
      </Box>
    );
  }

  // Use API data or provide defaults
  const walletBalance = plumber.wallet_balance ?? 0;
  const totalPointsEarned = plumber.total_earned ?? 0;
  const pointHistory = plumber.history ?? [];

  // Show empty state if no history
  const displayHistory = pointHistory.length > 0 ? pointHistory : [
    {
      date: new Date().toISOString(),
      code: "NO_DATA",
      type: "EARN" as const,
      points: 0,
    },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        minHeight: 0,
        background: "#101b20",
        borderLeft: "1px solid rgba(255,255,255,0.04)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header with Plumber Info */}
      <Box
          sx={{
          padding: "12px 16px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "transparent",
        }}
      >
          <Box display="flex" alignItems="center" mb={1.5}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              marginRight: "12px",
              background: "#0494dd",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "white",
            }}
          >
            {plumber.name?.charAt(0) || plumber.phone.charAt(0)}
          </Avatar>
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#ffffff", lineHeight: 1.2 }}
            >
              {plumber.name || "Plumber"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#b9c1c6", fontSize: "0.7rem" }}
            >
              {plumber.phone}
            </Typography>
          </Box>
        </Box>
        {plumber.address && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
                color: "#b9c1c6",
              fontSize: "0.65rem",
              lineHeight: 1.3,
            }}
          >
            {plumber.address}
          </Typography>
        )}
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "12px 16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Wallet Section */}
        <Box sx={{ paddingBottom: "12px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
          <Typography
            variant="caption"
            sx={{
              color: "#b9c1c6",
              fontSize: "0.6rem",
              fontWeight: 700,
              textTransform: "uppercase",
              display: "block",
              marginBottom: "8px",
              letterSpacing: "0.5px",
            }}
          >
            Wallet Balance
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#00a884",
              fontWeight: 700,
              fontSize: "1.25rem",
              marginBottom: "12px",
            }}
          >
            ₹{walletBalance.toLocaleString()}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: "#8696a1",
              fontSize: "0.6rem",
              fontWeight: 700,
              textTransform: "uppercase",
              display: "block",
              marginBottom: "4px",
              letterSpacing: "0.5px",
            }}
          >
            Total Points
          </Typography>
          <Typography variant="body2" sx={{ color: "#0494dd", fontWeight: 700 }}>
            {totalPointsEarned} pts
          </Typography>
        </Box>

        {/* Transaction History */}
        <Box sx={{ paddingTop: "12px" }}>
          <Typography
            variant="caption"
            sx={{
              color: "#b9c1c6",
              fontSize: "0.6rem",
              fontWeight: 700,
              textTransform: "uppercase",
              display: "block",
              marginBottom: "8px",
              letterSpacing: "0.5px",
            }}
          >
            Transaction History
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "0px", maxHeight: "300px", overflowY: "auto" }}>
            {displayHistory.map((entry, idx) => {
              const isEarn = entry.type === "EARN";
              const pointColor = isEarn ? "#00a884" : "#d3396c";
              const pointSign = isEarn ? "+" : "−";
              const formattedDate = entry.date ? formatTransactionDate(entry.date) : "No date";

              return (
                <Box
                  key={idx}
                  sx={{
                    padding: "8px 0",
                    borderBottom: idx < displayHistory.length - 1 ? "1px solid rgba(255,255,255,0.02)" : "none",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "3px" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: pointColor, fontSize: "0.7rem" }}>
                      {pointSign}{entry.points} pts
                    </Typography>

                    <Typography variant="caption" sx={{ color: "#b9c1c6", fontSize: "0.6rem", textAlign: "right" }}>
                      {formattedDate}
                    </Typography>
                  </Box>

                  <Typography variant="caption" sx={{ display: "block", color: "#b9c1c6", fontSize: "0.6rem", lineHeight: 1.2 }}>
                    {entry.code || "N/A"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileSection;
